import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useToastStore } from '../lib/stores';
import { TOAST_LAYER_CLASS } from '../lib/toastLayer';
import { WorkingSpinner } from './WorkingSpinner';

/** The toast layer's container, portalled to <body> so it ranks on `--z-toast`
 *  above every sheet, popover and menu rather than inside #root's own stacking
 *  context, where all of those painted over it (b-02055). Every house toast
 *  renders in one — see `lib/toastLayer`. */
export function ToastLayer({ children }: { children: ReactNode }) {
  return createPortal(<div className={TOAST_LAYER_CLASS}>{children}</div>, document.body);
}

export function ToastContainer() {
  const { toasts, removeToast } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <ToastLayer>
      {toasts.map((toast) => {
        const isProgress = toast.type === 'progress';
        return (
          <div
            key={toast.id}
            // Progress uses the info style; it just isn't auto-dismissed.
            className={`toast toast-${isProgress ? 'info' : toast.type}`}
            onClick={isProgress ? undefined : () => removeToast(toast.id)}
            role="alert"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {isProgress && <WorkingSpinner size={14} />}
              {toast.type === 'success' && '✓'}
              {toast.type === 'error' && '✕'}
              {toast.type === 'info' && 'ℹ'}
            </span>
            <span style={{ flex: 1 }}>{toast.message}</span>
            {/* Optional in-app shortcut (e.g. "Connect in Profile"). Dismisses the toast. */}
            {toast.action && !toast.action.href && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeToast(toast.id);
                  toast.action?.onClick?.();
                }}
              >
                {toast.action.label}
              </button>
            )}
            {toast.action?.href && (
              <Link
                to={toast.action.href}
                className="btn btn-secondary btn-sm"
                style={{ flexShrink: 0, whiteSpace: 'nowrap', textDecoration: 'none' }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeToast(toast.id);
                }}
              >
                {toast.action.label}
              </Link>
            )}
            {/* Progress toasts can't be dismissed mid-run; they resolve themselves. */}
            {!isProgress && (
              <button
                className="btn-ghost"
                style={{ padding: 'var(--space-1)', opacity: 0.6, fontSize: 'var(--text-xs)' }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeToast(toast.id);
                }}
              >
                ✕
              </button>
            )}
          </div>
        );
      })}
    </ToastLayer>
  );
}
